import React, { useEffect, useState } from "react";
import { Menu, X, Phone, Wrench } from "lucide-react";
import { BUSINESS } from "@/lib/business";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Reviews", href: "#reviews" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [setScrolled]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-[#E2E8F0] shadow-sm"
          : "bg-[#0B132B]/80 backdrop-blur-md border-b border-white/10"
      }`}
    >
      <div className="stripe-red" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#home"
            data-testid="logo-link"
            className="flex items-center gap-3 group"
          >
            <div
              className={`w-10 h-10 flex items-center justify-center border-2 ${
                scrolled ? "border-[#0B132B] bg-[#0B132B]" : "border-white bg-[#D32F2F]"
              } transition-colors`}
            >
              <Wrench className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col leading-tight">
              <span className={`font-display font-bold text-sm md:text-base ${scrolled ? "text-[#0B132B]" : "text-white"}`}>
                VETERAN'S AUTO
              </span>
              <span className={`font-mono-label text-[0.6rem] ${scrolled ? "text-[#D32F2F]" : "text-[#D32F2F]"}`}>
                Repair Center · Madison Heights
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={`font-medium text-sm tracking-wide transition-colors ${
                  scrolled
                    ? "text-[#0B132B] hover:text-[#D32F2F]"
                    : "text-white/90 hover:text-white"
                }`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${BUSINESS.phoneDial}`}
              data-testid="header-call-link"
              className={`flex items-center gap-2 text-sm font-semibold transition-colors ${
                scrolled ? "text-[#0B132B] hover:text-[#D32F2F]" : "text-white hover:text-[#D32F2F]"
              }`}
            >
              <Phone className="w-4 h-4" strokeWidth={2.5} />
              {BUSINESS.phoneDisplay}
            </a>
            <a
              href="#contact"
              data-testid="header-schedule-btn"
              className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-semibold py-3 px-6 rounded-none text-sm tracking-wide transition-colors"
            >
              Schedule Service
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 ${scrolled ? "text-[#0B132B]" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile panel */}
        {open && (
          <div data-testid="mobile-menu" className="lg:hidden border-t border-[#E2E8F0] bg-white py-4 px-2">
            <nav className="flex flex-col gap-1">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                  className="py-3 px-3 text-[#0B132B] font-medium text-base hover:bg-[#F8F9FA]"
                >
                  {n.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-schedule-btn"
                className="mt-3 bg-[#D32F2F] hover:bg-[#B71C1C] text-white text-center font-semibold py-3 px-6 rounded-none"
              >
                Schedule Service
              </a>
              <a
                href={`tel:${BUSINESS.phoneDial}`}
                onClick={() => setOpen(false)}
                data-testid="mobile-call-btn"
                className="bg-[#0B132B] hover:bg-[#141e3c] text-white text-center font-semibold py-3 px-6 rounded-none flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" /> {BUSINESS.phoneDisplay}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
